import {
  lazy,
  memo,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import type { ActiveView } from "./routes.js";
import type {
  AgentAnswerCitation,
  AgentChat,
  AgentChatSummary,
  AgentConversationIntent,
  AgentConversationReply,
  AgentDraftIntent,
  AgentGeneratedUiComponent,
  AgentGroundedAnswer,
  AgentProgressStep,
  PolyfutureApiClient,
} from "./api.js";
import type { BookRecord, ScenarioDraftInput, ScenarioPolarity } from "./types.js";
import { ProductPage } from "./ProductSurface.js";
import { AttachmentChips, SentAttachments, useComposerAttachments } from "./ComposerAttachments.js";
import {
  ATTACHMENT_ACCEPT,
  attachmentSendBlock,
  defaultAttachmentQuestion,
} from "./attachment-rules.js";

export interface ParsedHolding {
  readonly symbol: string;
  readonly name: string;
  readonly quantity: number | undefined;
}

export interface AgentDraft {
  readonly title: string;
  readonly lines: readonly string[];
  readonly cta: string;
  readonly hint: string;
  readonly destination: ActiveView;
  readonly holdings?: readonly ParsedHolding[];
  readonly statedOrigin?: string;
  readonly payload?: unknown;
}

export interface AgentTurn {
  readonly id: string;
  readonly role: "user" | "agent";
  readonly text: string;
  readonly draft?: AgentDraft;
  readonly drafts?: readonly AgentDraft[];
  readonly grounding?: NonNullable<AgentConversationReply["answer"]>;
  /** ADR-092. Files sent with a question, by name. */
  readonly attachments?: readonly { readonly name: string }[];
  /** V-A2. The sources this answer read, each marked done or failed. */
  readonly steps?: readonly AgentProgressStep[];
}

/** V-A7. Something an answer built, shown in the artefact column beside the conversation. */
export interface AskArtefact {
  readonly id: string;
  readonly kind: "whatif" | "research" | "trade";
  readonly title: string;
  readonly turnId: string;
  readonly question: string;
  readonly draft?: AgentDraft;
  readonly answer?: AgentGroundedAnswer;
  /** The stored answer a trade draft came from, which its proposal records. */
  readonly answerId?: string;
  /** The sources that failed while it was built, which the artefact must disclose. */
  readonly failedSources: readonly string[];
}

/** What the server confirmed for an artefact. Nothing is marked saved before that. */
export type AskArtefactSaved =
  | {
      readonly kind: "whatif";
      readonly bookId: string;
      readonly scenarioId: string;
      readonly ran: boolean;
    }
  | { readonly kind: "trade"; readonly goalId: string; readonly proposalId: string };

const AskArtefactColumn = lazy(async () => ({
  default: (await import("./AskArtefacts.js")).AskArtefactColumn,
}));

const ASK_SUGGESTIONS = [
  ["What-if", "What if the Fed cuts rates faster than the market expects?"],
  ["Trade", "Propose a trade: buy $3,000 of JPM for my growth goal"],
  ["Research", "How has TSMC monthly revenue trended this year?"],
] as const;

export interface AgentRecentChat {
  readonly id: string;
  readonly title: string;
  readonly updatedAt: string;
}

interface StoredChat extends AgentRecentChat {
  readonly turns: readonly AgentTurn[];
}

interface AgentStore {
  readonly chats: readonly StoredChat[];
}

interface AgentReply {
  readonly text: string;
  readonly draft?: AgentDraft;
}

interface AgentViewProps {
  readonly client: PolyfutureApiClient;
  readonly storageScope: string;
  readonly chatId: string;
  /** Reopened from the recent list: read from the server, which holds the full history. */
  readonly fromHistory?: boolean;
  readonly selectedBookId?: string;
  readonly onChatsChanged: (recents: readonly AgentRecentChat[]) => void;
  readonly onOpenDraft: (draft: AgentDraft) => void;
  readonly openingDraft?: boolean;
  /** Actions under a stored answer, such as "Save to Research" (ADR-091). */
  readonly renderAnswerActions?: (answer: AgentGroundedAnswer, question: string) => ReactNode;
  /** Told whether a conversation is open, so the shell can narrow the rail to icons. */
  readonly onConversationOpenChange?: (open: boolean) => void;
  /** A question handed over from Market research: filled in, and sent only when `send` is set. */
  readonly prefill?: { readonly text: string; readonly send: boolean; readonly nonce: number };
  /** Told once the hand-over is used, so reopening Ask does not repeat it. */
  readonly onPrefillUsed?: () => void;
  /** V-A3. Portfolios a what-if draft can be saved to, and whether this user may save. */
  readonly books?: readonly BookRecord[];
  readonly canWrite?: boolean;
  /** Saves a what-if draft and returns the new what-if's id, only after the server confirms. */
  readonly onSaveWhatIf?: (bookId: string, input: ScenarioDraftInput) => Promise<string>;
  readonly onOpenWhatIf?: (bookId: string, scenarioId: string) => void;
  /** Opens a goal in the Trader, after a trade proposal is saved (V-A4). */
  readonly onOpenGoal?: (goalId: string) => void;
}

const INSTRUMENT_UNIVERSE = [
  ["AAPL", "Apple"],
  ["MSFT", "Microsoft"],
  ["NVDA", "Nvidia"],
  ["VOO", "Vanguard S&P 500 ETF"],
  ["TSLA", "Tesla"],
  ["AMZN", "Amazon"],
  ["GOOGL", "Alphabet class A"],
  ["JPM", "JPMorgan Chase"],
  ["XOM", "Exxon Mobil"],
  ["SCHD", "Schwab US Dividend ETF"],
  ["BND", "Vanguard Total Bond ETF"],
  ["TSM", "TSMC ADR"],
  ["MU", "Micron Technology"],
  ["COST", "Costco Wholesale"],
  ["SMH", "VanEck Semiconductor ETF"],
  ["O", "Realty Income"],
] as const;

const INSTRUMENT_BY_SYMBOL: ReadonlyMap<string, { readonly name: string }> = new Map(
  INSTRUMENT_UNIVERSE.map(([symbol, name]) => [symbol, { name }]),
);
const GUARDED_SYMBOLS = new Set([
  "COST",
  "ON",
  "ALL",
  "KEY",
  "IT",
  "SO",
  "LOW",
  "NOW",
  "RUN",
  "FAST",
]);
export function AgentView({
  client,
  storageScope,
  chatId,
  fromHistory = false,
  selectedBookId,
  onChatsChanged,
  onOpenDraft,
  openingDraft = false,
  renderAnswerActions,
  onConversationOpenChange,
  prefill,
  onPrefillUsed,
  books = [],
  canWrite = false,
  onSaveWhatIf,
  onOpenWhatIf,
  onOpenGoal,
}: AgentViewProps) {
  const storageKey = useMemo(() => agentStorageKey(storageScope), [storageScope]);
  const [turns, setTurns] = useState<readonly AgentTurn[]>(
    () => readAgentStore(storageKey).chats.find((chat) => chat.id === chatId)?.turns ?? [],
  );
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [streamStatus, setStreamStatus] = useState("Preparing your request…");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string>();
  const transcriptEnd = useRef<HTMLDivElement>(null);
  const pendingDelta = useRef("");
  const animationFrame = useRef<number | undefined>(undefined);
  const activeRequest = useRef<AbortController | undefined>(undefined);
  const retryTurns = useRef<readonly AgentTurn[] | undefined>(undefined);
  const receivedText = useRef("");
  const [following, setFollowing] = useState(true);
  const attachments = useComposerAttachments(client);
  const retryAttachmentIds = useRef<readonly string[]>([]);
  const [attachmentNotice, setAttachmentNotice] = useState<string>();
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const attachmentBlock = attachmentSendBlock(attachments.items);
  const conversationOpen = turns.length > 0 || pending;
  const [steps, setSteps] = useState<readonly AgentProgressStep[]>([]);
  const stepsRef = useRef<readonly AgentProgressStep[]>([]);
  const [shownArtefactId, setShownArtefactId] = useState<string>();
  const [artefactClosed, setArtefactClosed] = useState(false);
  const [phoneTab, setPhoneTab] = useState<"conversation" | "artefact">("conversation");
  const [savedArtefacts, setSavedArtefacts] = useState<Readonly<Record<string, AskArtefactSaved>>>(
    {},
  );
  const artefacts = useMemo(() => askArtefacts(turns), [turns]);
  const shownArtefact =
    artefacts.find((artefact) => artefact.id === shownArtefactId) ?? artefacts.at(-1);
  const artefactOpen = shownArtefact !== undefined && !artefactClosed;

  function showArtefact(id: string) {
    setShownArtefactId(id);
    setArtefactClosed(false);
    setPhoneTab("artefact");
  }

  useEffect(() => {
    onConversationOpenChange?.(conversationOpen);
  }, [conversationOpen, onConversationOpenChange]);

  useEffect(() => () => onConversationOpenChange?.(false), [onConversationOpenChange]);

  useEffect(() => {
    setTurns(readAgentStore(storageKey).chats.find((chat) => chat.id === chatId)?.turns ?? []);
    setDraft("");
    setPending(false);
    setStreamingText("");
    setStreamStatus("Preparing your request…");
    setElapsedSeconds(0);
    setError(undefined);
    retryTurns.current = undefined;
    setFollowing(true);
    setSteps([]);
    stepsRef.current = [];
    setShownArtefactId(undefined);
    setArtefactClosed(false);
    setPhoneTab("conversation");
    setSavedArtefacts({});
    return () => {
      const request = activeRequest.current;
      activeRequest.current = undefined;
      request?.abort();
      if (animationFrame.current !== undefined) cancelAnimationFrame(animationFrame.current);
      animationFrame.current = undefined;
      pendingDelta.current = "";
    };
  }, [chatId, storageKey]);

  // ADR-090: the server's history is authoritative; the local copy only paints the first frame.
  // A send that starts before the server answers wins, so a reply is never overwritten.
  useEffect(() => {
    if (!fromHistory) return undefined;
    let current = true;
    void client.getAgentChat(chatId).then(
      (chat) => {
        if (!current || activeRequest.current !== undefined) return;
        const serverTurns = turnsFromServerChat(chat);
        setTurns(serverTurns);
        const lastAskedAt = chat.messages.at(-1)?.askedAt ?? new Date().toISOString();
        persistChat(storageKey, chatId, serverTurns, lastAskedAt, onChatsChanged);
      },
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [chatId, client, fromHistory, onChatsChanged, storageKey]);

  useEffect(() => {
    void client.prewarmAgent();
  }, [client]);

  // A hand-over runs once per nonce. It waits a tick so it lands after the chat's own reset, and
  // its cleanup cancels it, so React's development double run neither drops nor repeats it.
  useEffect(() => {
    if (prefill === undefined) return undefined;
    const timer = window.setTimeout(() => {
      if (prefill.send) void submit(prefill.text);
      else setDraft(prefill.text);
      onPrefillUsed?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [prefill?.nonce]);

  useEffect(() => {
    if (!pending) return undefined;
    const startedAt = Date.now();
    const timer = window.setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1_000)),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [pending]);

  useEffect(() => {
    if (following) transcriptEnd.current?.scrollIntoView({ behavior: "auto", block: "nearest" });
  }, [following, streamingText, turns.length, streamStatus, steps.length]);

  useEffect(() => {
    const onScroll = () => {
      const bottom = transcriptEnd.current?.getBoundingClientRect().bottom;
      if (bottom !== undefined) setFollowing(bottom <= window.innerHeight + 160);
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, []);

  useEffect(
    () => () => {
      if (animationFrame.current !== undefined) cancelAnimationFrame(animationFrame.current);
    },
    [],
  );

  function appendStreamingDelta(delta: string) {
    receivedText.current += delta;
    pendingDelta.current += delta;
    if (animationFrame.current !== undefined) return;
    animationFrame.current = requestAnimationFrame(() => {
      const next = pendingDelta.current;
      pendingDelta.current = "";
      animationFrame.current = undefined;
      setStreamingText((current) => current + next);
    });
  }

  async function submit(text: string, retry = false) {
    if (activeRequest.current !== undefined) return;
    if (!retry && attachmentBlock !== undefined) {
      setAttachmentNotice(attachmentBlock);
      return;
    }
    const sending = retry ? [] : attachments.items;
    const message =
      text.trim().length > 0
        ? text.trim()
        : sending.length > 0
          ? defaultAttachmentQuestion(sending.every((item) => item.photo))
          : "";
    if (message.length === 0) return;
    setAttachmentNotice(undefined);
    if (!retry) {
      attachments.take();
      retryAttachmentIds.current = sending.flatMap((item) =>
        item.attachmentId === undefined ? [] : [item.attachmentId],
      );
    }
    const attachmentIds = retryAttachmentIds.current;
    const request = new AbortController();
    activeRequest.current = request;
    const isCurrent = () => activeRequest.current === request;
    const now = new Date().toISOString();
    const userTurns: readonly AgentTurn[] =
      retry && retryTurns.current !== undefined
        ? retryTurns.current
        : [
            ...turns,
            {
              id: `${now}:user`,
              role: "user",
              text: message,
              ...(sending.length === 0
                ? {}
                : { attachments: sending.map((item) => ({ name: item.file.name })) }),
            },
          ];
    retryTurns.current = userTurns;
    setTurns(userTurns);
    if (!retry) setDraft("");
    setFollowing(true);
    setPending(true);
    setStreamingText("");
    pendingDelta.current = "";
    receivedText.current = "";
    setStreamStatus("Preparing your request…");
    setElapsedSeconds(0);
    setError(undefined);
    setSteps([]);
    stepsRef.current = [];
    persistChat(storageKey, chatId, userTurns, now, onChatsChanged);
    try {
      const result = await client.streamAgentMessage(
        {
          chatId,
          turns: agentConversationTurns(userTurns).map((turn) => ({
            role: turn.role === "agent" ? "assistant" : "user",
            text: turn.text,
          })),
          ...(selectedBookId === undefined ? {} : { selectedBookId }),
          ...(attachmentIds.length === 0 ? {} : { attachmentIds }),
        },
        {
          onDelta: (delta) => {
            if (!isCurrent()) return;
            setStreamStatus("Writing the answer…");
            appendStreamingDelta(delta);
          },
          onStatus: (status) => {
            if (!isCurrent()) return;
            setStreamStatus(status.message);
            const step = status.step;
            if (step !== undefined) {
              stepsRef.current = upsertStep(stepsRef.current, step);
              setSteps(stepsRef.current);
            }
          },
        },
        request.signal,
      );
      if (!isCurrent()) return;
      const modelDrafts =
        result.answer?.draftIntents.map((intent) =>
          draftFromModelIntent(intent, undefined, message),
        ) ?? [];
      const stagedDraft =
        modelDrafts.length === 0 ? draftForIntent(message, result.intent) : undefined;
      const completedAt = new Date().toISOString();
      const nextTurns: readonly AgentTurn[] = [
        ...userTurns,
        {
          id: `${completedAt}:agent`,
          role: "agent",
          text: result.reply,
          ...(stagedDraft === undefined ? {} : { draft: stagedDraft }),
          ...(modelDrafts.length === 0 ? {} : { drafts: modelDrafts }),
          ...(result.answer === undefined ? {} : { grounding: result.answer }),
          ...(stepsRef.current.length === 0 ? {} : { steps: settledSteps(stepsRef.current) }),
        },
      ];
      setTurns(nextTurns);
      // Show what this answer built: a what-if draft first, then a research result.
      const built = askArtefacts(nextTurns).filter(
        (artefact) => artefact.turnId === `${completedAt}:agent`,
      );
      const first =
        built.find((artefact) => artefact.kind === "trade") ??
        built.find((artefact) => artefact.kind === "whatif") ??
        built[0];
      if (first !== undefined) {
        setShownArtefactId(first.id);
        setArtefactClosed(false);
      }
      setStreamingText("");
      persistChat(storageKey, chatId, nextTurns, completedAt, onChatsChanged);
    } catch (caught) {
      if (!isCurrent()) return;
      setStreamingText(receivedText.current);
      setError(
        request.signal.aborted
          ? "Reply stopped. Text below is incomplete. Research jobs already submitted may still finish."
          : caught instanceof Error
            ? caught.message
            : "The agent could not answer. Your message is saved; try again.",
      );
    } finally {
      if (isCurrent()) {
        if (animationFrame.current !== undefined) cancelAnimationFrame(animationFrame.current);
        animationFrame.current = undefined;
        pendingDelta.current = "";
        activeRequest.current = undefined;
        setPending(false);
      }
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void submit(draft);
  }

  function composer(landing: boolean) {
    return (
      <form
        className={
          dragging
            ? `agent-composer pf-chat-composer composer-dragging${landing ? " pf-ask-composer-landing" : ""}`
            : `agent-composer pf-chat-composer${landing ? " pf-ask-composer-landing" : ""}`
        }
        onSubmit={handleSubmit}
        onDragOver={(event) => {
          if (!event.dataTransfer.types.includes("Files")) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          if (event.dataTransfer.files.length === 0) return;
          event.preventDefault();
          setDragging(false);
          attachments.add([...event.dataTransfer.files]);
        }}
      >
        <AttachmentChips
          items={attachments.items}
          onRemove={attachments.remove}
          onRetry={attachments.retry}
        />
        {dragging ? <p className="composer-drop-hint">Drop to attach</p> : null}
        {[...attachments.errors, ...(attachmentNotice === undefined ? [] : [attachmentNotice])].map(
          (message) => (
            <p className="composer-attachment-error" role="alert" key={message}>
              ⚠ {message}
            </p>
          ),
        )}
        <label className="visually-hidden" htmlFor="agent-message">
          Ask Polyfuture
        </label>
        <textarea
          id="agent-message"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={
            landing
              ? "Ask about a company, your holdings, or a what-if…"
              : "Ask a follow-up, or ask for a change to the draft…"
          }
          autoComplete="off"
          rows={landing ? 3 : 2}
          onPaste={(event) => {
            const pasted = [...event.clipboardData.files].filter((file) =>
              file.type.startsWith("image/"),
            );
            if (pasted.length === 0) return;
            event.preventDefault();
            attachments.add(pasted);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              if (!pending) void submit(draft);
            }
          }}
        />
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          hidden
          onChange={(event) => {
            attachments.add([...(event.target.files ?? [])]);
            event.target.value = "";
          }}
        />
        <button
          type="button"
          className="composer-paperclip"
          aria-label="Attach a photo or file"
          title="Attach a photo or file"
          disabled={pending}
          onClick={() => fileInput.current?.click()}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              d="M21 11.5l-8.5 8.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {!pending ? (
          <button
            className="button button-primary"
            type="submit"
            disabled={
              (draft.trim().length === 0 && attachments.items.length === 0) ||
              pending ||
              attachmentBlock !== undefined
            }
            title={attachmentBlock}
          >
            Send
          </button>
        ) : null}
        {pending ? (
          <button className="button" type="button" onClick={() => activeRequest.current?.abort()}>
            Stop reply
          </button>
        ) : null}
        <small>
          {landing ? "Photos, PDFs, CSV or Excel · " : ""}
          {selectedBookId === undefined ? "All available context" : "Selected portfolio context"}
          {" · "}sources and provenance retained
        </small>
      </form>
    );
  }

  const failureNote = (
    <p className="pf-ask-step-note">
      The source returned an error. The draft is built without it and says so.
    </p>
  );

  if (!conversationOpen) {
    return (
      <ProductPage className="pf-chat-page agent-screen">
        <div className="pf-ask-landing" aria-labelledby="agent-heading">
          <header className="pf-chat-intro">
            <h1 id="agent-heading">Understand what could change.</h1>
            <p>Ask about your holdings, test an assumption, or explore a possible future.</p>
          </header>
          {composer(true)}
          <ul className="pf-ask-suggestions" aria-label="Suggested questions">
            {ASK_SUGGESTIONS.map(([kind, question]) => (
              <li key={kind}>
                <button type="button" disabled={pending} onClick={() => void submit(question)}>
                  <span>{kind}</span>
                  {question}
                </button>
              </li>
            ))}
          </ul>
          {openingDraft ? <p role="status">Opening your draft…</p> : null}
        </div>
      </ProductPage>
    );
  }

  return (
    <div
      className="pf-ask-workspace"
      data-artefact-open={artefactOpen ? "true" : "false"}
      data-phone-tab={artefactOpen ? phoneTab : "conversation"}
    >
      <h1 className="visually-hidden" id="agent-heading">
        Ask Polyfuture
      </h1>
      {artefactOpen ? (
        <div className="pf-ask-phone-tabs" role="tablist" aria-label="Conversation or result">
          <button
            type="button"
            role="tab"
            aria-selected={phoneTab === "conversation"}
            onClick={() => setPhoneTab("conversation")}
          >
            Conversation
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={phoneTab === "artefact"}
            onClick={() => setPhoneTab("artefact")}
          >
            {artefactKindLabel(shownArtefact.kind)}
          </button>
        </div>
      ) : null}
      <section className="pf-ask-chat" aria-label="Conversation">
        <div className="agent-transcript pf-conversation pf-ask-thread" aria-live="polite">
          {turns.map((turn, index) => {
            const built = artefacts.filter((artefact) => artefact.turnId === turn.id);
            const researchBuilt = built.some((artefact) => artefact.kind === "research");
            const otherDrafts = (
              turn.drafts ?? (turn.draft === undefined ? [] : [turn.draft])
            ).filter(
              (draft) => draft.destination !== "new_scenario" && draft.destination !== "trading",
            );
            const failed = (turn.steps ?? []).filter((step) => step.status === "failed");
            return (
              <article className={`agent-turn agent-turn-${turn.role}`} key={turn.id}>
                {turn.role === "agent" ? (
                  <p className="pf-agent-byline">
                    <span>P</span> Polyfuture
                  </p>
                ) : null}
                {turn.role === "agent" && turn.steps !== undefined ? (
                  <ProgressSteps steps={turn.steps} failureNote={failureNote} />
                ) : null}
                {turn.role === "agent" ? (
                  <AgentResponse text={turn.text} />
                ) : (
                  <>
                    <SentAttachments attachments={turn.attachments ?? []} />
                    <p className="agent-bubble">{turn.text}</p>
                  </>
                )}
                {failed.length === 0 ? null : (
                  <p className="pf-ask-disclosure">
                    Built without {joinLabels(failed.map((step) => lowerFirst(step.label)))}, which
                    returned an error.
                  </p>
                )}
                {turn.grounding?.generatedUi === undefined || researchBuilt ? null : (
                  <fieldset className="agent-action-boundary" disabled={openingDraft}>
                    <AgentGeneratedUiView answer={turn.grounding} onOpenDraft={onOpenDraft} />
                  </fieldset>
                )}
                {turn.grounding === undefined ? null : <AgentGrounding answer={turn.grounding} />}
                {turn.grounding === undefined || renderAnswerActions === undefined || researchBuilt
                  ? null
                  : renderAnswerActions(turn.grounding, questionBefore(turns, index))}
                {built.map((artefact) => (
                  <button
                    type="button"
                    className="pf-ask-artefact-card"
                    data-shown={artefactOpen && shownArtefact.id === artefact.id ? "true" : "false"}
                    key={artefact.id}
                    onClick={() => showArtefact(artefact.id)}
                  >
                    <small>
                      {artefactKindLabel(artefact.kind)}
                      {artefactOpen && shownArtefact.id === artefact.id ? (
                        <>
                          <span className="pf-ask-wide-only"> · shown on the right</span>
                          <span className="pf-ask-phone-only"> · open</span>
                        </>
                      ) : (
                        " · open"
                      )}
                    </small>
                    <strong>{artefact.title}</strong>
                    <span>
                      {savedArtefacts[artefact.id] === undefined ? "Ready to review" : "Saved"}
                    </span>
                  </button>
                ))}
                <fieldset className="agent-action-boundary" disabled={openingDraft}>
                  <AgentDraftCards drafts={otherDrafts} onOpenDraft={onOpenDraft} />
                </fieldset>
              </article>
            );
          })}
          {pending ? (
            <article className="agent-turn agent-turn-agent" aria-label={streamStatus}>
              <p className="pf-agent-byline">
                <span>P</span> Polyfuture · working
              </p>
              <ProgressSteps steps={steps} failureNote={failureNote} />
              <div className="agent-progress" role="status">
                <span className="agent-progress-orbit" aria-hidden="true" />
                <span>{streamStatus}</span>
                <time>{elapsedSeconds}s</time>
              </div>
              {streamingText.length === 0 ? (
                <div className="agent-bubble agent-answer agent-bubble-pending" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
              ) : (
                <AgentResponse text={streamingText} streaming />
              )}
            </article>
          ) : null}
          {error === undefined ? null : (
            <div className="agent-error" role="alert">
              <p>{error}</p>
              {streamingText.length === 0 ? null : (
                <>
                  <p>Incomplete reply — not a final answer</p>
                  <AgentResponse text={streamingText} />
                </>
              )}
              <button
                className="button"
                disabled={pending}
                onClick={() => void submit(retryTurns.current?.at(-1)?.text ?? "", true)}
              >
                Retry reply
              </button>
            </div>
          )}
          {!following && pending ? (
            <button className="button" onClick={() => setFollowing(true)}>
              Follow latest reply
            </button>
          ) : null}
          {openingDraft ? <p role="status">Opening your draft…</p> : null}
          <div ref={transcriptEnd} />
        </div>
        {composer(false)}
      </section>
      {artefactOpen ? (
        <aside className="pf-ask-artefacts" aria-label={artefactKindLabel(shownArtefact.kind)}>
          <Suspense fallback={<p role="status">Opening…</p>}>
            <AskArtefactColumn
              client={client}
              artefacts={artefacts}
              shown={shownArtefact}
              saved={savedArtefacts}
              books={books}
              {...(selectedBookId === undefined ? {} : { selectedBookId })}
              canWrite={canWrite}
              onShow={showArtefact}
              onClose={() => {
                setArtefactClosed(true);
                setPhoneTab("conversation");
              }}
              onSaved={(id, saved) => setSavedArtefacts((current) => ({ ...current, [id]: saved }))}
              {...(onSaveWhatIf === undefined ? {} : { onSaveWhatIf })}
              {...(onOpenWhatIf === undefined ? {} : { onOpenWhatIf })}
              {...(onOpenGoal === undefined ? {} : { onOpenGoal })}
              {...(renderAnswerActions === undefined ? {} : { renderAnswerActions })}
              onOpenDraft={onOpenDraft}
            />
          </Suspense>
        </aside>
      ) : null}
    </div>
  );
}

function ProgressSteps({
  steps,
  failureNote,
}: {
  readonly steps: readonly AgentProgressStep[];
  readonly failureNote: ReactNode;
}) {
  if (steps.length === 0) return null;
  return (
    <ul className="pf-ask-steps" aria-label="Sources checked">
      {steps.map((step) => (
        <li key={step.stepId} data-status={step.status}>
          <span aria-hidden="true">
            {step.status === "done" ? "✓" : step.status === "failed" ? "!" : "·"}
          </span>
          <span>
            {step.label}
            <span className="visually-hidden">
              {step.status === "done"
                ? " (done)"
                : step.status === "failed"
                  ? " (failed)"
                  : " (running)"}
            </span>
          </span>
          {step.status === "failed" ? failureNote : null}
        </li>
      ))}
    </ul>
  );
}

export function artefactKindLabel(kind: AskArtefact["kind"]): string {
  return kind === "whatif"
    ? "What-if draft"
    : kind === "trade"
      ? "Trade proposal"
      : "Research result";
}

/** Keeps one line per source: a later status replaces the earlier one for the same step. */
export function upsertStep(
  steps: readonly AgentProgressStep[],
  step: AgentProgressStep,
): readonly AgentProgressStep[] {
  const index = steps.findIndex((existing) => existing.stepId === step.stepId);
  if (index === -1) return [...steps, step];
  return steps.map((existing, position) => (position === index ? step : existing));
}

/**
 * A finished answer has no running sources. A step the runtime never closed is shown as done only
 * if the answer completed; it was not reported as failed.
 */
export function settledSteps(steps: readonly AgentProgressStep[]): readonly AgentProgressStep[] {
  return steps.map((step) => (step.status === "running" ? { ...step, status: "done" } : step));
}

/** V-A7. The artefacts a conversation built, in order: what-if drafts and research results. */
export function askArtefacts(turns: readonly AgentTurn[]): readonly AskArtefact[] {
  const artefacts: AskArtefact[] = [];
  turns.forEach((turn, index) => {
    if (turn.role !== "agent") return;
    const question = questionBefore(turns, index);
    const failedSteps = (turn.steps ?? [])
      .filter((step) => step.status === "failed")
      .map((step) => step.label);
    const failedRuns =
      turn.grounding?.toolRuns.some((run) => run.status === "failed" || run.status === "denied") ??
      false;
    const failedSources = failedSteps.length > 0 ? failedSteps : failedRuns ? ["A source"] : [];
    const drafts = turn.drafts ?? (turn.draft === undefined ? [] : [turn.draft]);
    drafts.forEach((draft, draftIndex) => {
      if (draft.destination === "trading" && turn.grounding !== undefined) {
        const payload = isAgentRecord(draft.payload) ? draft.payload : {};
        const symbol = typeof payload["symbol"] === "string" ? payload["symbol"] : "";
        const side = payload["side"] === "sell" ? "Sell" : "Buy";
        artefacts.push({
          id: `${turn.id}:trade:${draftIndex}`,
          kind: "trade",
          title: `${side} ${symbol}`.trim(),
          turnId: turn.id,
          question,
          draft,
          answerId: turn.grounding.answerId,
          failedSources,
        });
        return;
      }
      if (draft.destination !== "new_scenario") return;
      const input = scenarioInputForAgentDraft(draft);
      artefacts.push({
        id: `${turn.id}:whatif:${draftIndex}`,
        kind: "whatif",
        title: input?.name ?? draft.title,
        turnId: turn.id,
        question,
        draft,
        failedSources,
      });
    });
    const answer = turn.grounding;
    if (answer !== undefined && (answer.generatedUi !== undefined || answer.claims.length > 0)) {
      artefacts.push({
        id: `${turn.id}:research`,
        kind: "research",
        title: answer.generatedUi?.title ?? oneLine(question).slice(0, 120),
        turnId: turn.id,
        question,
        answer,
        failedSources,
      });
    }
  });
  return artefacts;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function joinLabels(labels: readonly string[]): string {
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)!}`;
}

const MAX_CONVERSATION_TURNS = 12;
const MAX_CONVERSATION_CHARACTERS = 16_000;

export function agentConversationTurns(turns: readonly AgentTurn[]): readonly AgentTurn[] {
  const selected: AgentTurn[] = [];
  let characters = 0;
  for (
    let index = turns.length - 1;
    index >= 0 && selected.length < MAX_CONVERSATION_TURNS;
    index--
  ) {
    const turn = turns[index]!;
    if (selected.length > 0 && characters + turn.text.length > MAX_CONVERSATION_CHARACTERS) break;
    selected.unshift(turn);
    characters += turn.text.length;
  }
  return selected;
}

const AgentResponse = memo(function AgentResponse({
  text,
  streaming = false,
}: {
  readonly text: string;
  readonly streaming?: boolean;
}) {
  return (
    <div className={`agent-bubble agent-answer${streaming ? " agent-answer-streaming" : ""}`}>
      {messageBlocks(text).map((block, index) => {
        if (block.kind === "heading") {
          return <h3 key={index}>{inlineMessage(block.text)}</h3>;
        }
        if (block.kind === "list") {
          const List = block.ordered ? "ol" : "ul";
          return (
            <List key={index}>
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{inlineMessage(item)}</li>
              ))}
            </List>
          );
        }
        if (block.kind === "quote") {
          return <blockquote key={index}>{inlineMessage(block.text)}</blockquote>;
        }
        return <p key={index}>{inlineMessage(block.text)}</p>;
      })}
    </div>
  );
});

type MessageBlock =
  | { readonly kind: "heading" | "paragraph" | "quote"; readonly text: string }
  | { readonly kind: "list"; readonly ordered: boolean; readonly items: readonly string[] };

export function messageBlocks(text: string): readonly MessageBlock[] {
  const blocks: MessageBlock[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | undefined;
  const flushParagraph = () => {
    if (paragraph.length > 0) blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };
  const flushList = () => {
    if (list !== undefined) blocks.push({ kind: "list", ...list });
    list = undefined;
  };
  for (const rawLine of text.replace(/\r/g, "").split("\n")) {
    const line = rawLine.trim();
    if (line.length === 0) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = /^#{1,4}\s+(.+)$/.exec(line);
    const unordered = /^[-*]\s+(.+)$/.exec(line);
    const ordered = /^\d+[.)]\s+(.+)$/.exec(line);
    if (heading !== null) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "heading", text: heading[1]! });
    } else if (unordered !== null || ordered !== null) {
      flushParagraph();
      const isOrdered = ordered !== null;
      if (list === undefined || list.ordered !== isOrdered) {
        flushList();
        list = { ordered: isOrdered, items: [] };
      }
      list.items.push((unordered ?? ordered)![1]!);
    } else if (line.startsWith("> ")) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "quote", text: line.slice(2) });
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return blocks.length === 0 ? [{ kind: "paragraph", text }] : blocks;
}

function inlineMessage(text: string): ReactNode[] {
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)]+\))/g);
  return tokens.filter(Boolean).map((token, index) => {
    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={index}>{token.slice(2, -2)}</strong>;
    }
    if (token.startsWith("`") && token.endsWith("`")) {
      return <code key={index}>{token.slice(1, -1)}</code>;
    }
    const link = /^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/.exec(token);
    if (link !== null) {
      return (
        <a key={index} href={link[2]} target="_blank" rel="noreferrer">
          {link[1]}
        </a>
      );
    }
    return token;
  });
}

function AgentDraftCards({
  drafts,
  onOpenDraft,
}: {
  readonly drafts: readonly AgentDraft[];
  readonly onOpenDraft: (draft: AgentDraft) => void;
}) {
  if (drafts.length === 0) return null;
  return (
    <div className="agent-draft-stack">
      {drafts.map((draft, index) => (
        <section
          className="agent-staged-card"
          aria-label={draft.title}
          key={`${draft.title}:${index}`}
        >
          <h3>{draft.title}</h3>
          <div>
            {draft.lines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
          <footer>
            <button
              className="button button-primary"
              type="button"
              onClick={() => onOpenDraft(draft)}
            >
              {draft.cta}
            </button>
            <span>{draft.hint}</span>
          </footer>
        </section>
      ))}
    </div>
  );
}

export function AgentGeneratedUiView({
  answer,
  onOpenDraft,
}: {
  readonly answer: AgentGroundedAnswer;
  readonly onOpenDraft: (draft: AgentDraft) => void;
}) {
  const ui = answer.generatedUi;
  if (ui === undefined) return null;
  return (
    <section className="agent-generated-ui" aria-label={ui.title ?? "Generated analysis view"}>
      {ui.title === undefined ? null : <h3>{ui.title}</h3>}
      <div className="agent-generated-grid">
        {ui.components.map((component) => (
          <AgentGeneratedComponent
            component={component}
            answer={answer}
            onOpenDraft={onOpenDraft}
            key={component.componentId}
          />
        ))}
      </div>
      <p className="agent-generated-boundary">Sources are linked below each result.</p>
    </section>
  );
}

/** What a generated component needs from its answer: the claims and sources it links to. */
type AnswerSources = Pick<AgentGroundedAnswer, "claims" | "citations" | "draftIntents">;

/** One saved figure of a research result (ADR-091), with its sources, outside a conversation. */
export function ResearchFigure({
  component,
  sources,
}: {
  readonly component: AgentGeneratedUiComponent;
  readonly sources: Pick<AgentGroundedAnswer, "claims" | "citations">;
}) {
  if (component.kind === "scenario_controls") return null;
  return (
    <AgentGeneratedComponent
      component={component}
      answer={{ ...sources, draftIntents: [] }}
      onOpenDraft={() => undefined}
    />
  );
}

export { CitationChip };

function AgentGeneratedComponent({
  component,
  answer,
  onOpenDraft,
}: {
  readonly component: AgentGeneratedUiComponent;
  readonly answer: AnswerSources;
  readonly onOpenDraft: (draft: AgentDraft) => void;
}) {
  if (component.kind === "metric")
    return (
      <article className="agent-generated-card agent-generated-metric">
        <small>{component.label}</small>
        <strong>
          {displayValue(component.value)}
          {component.unit === undefined ? "" : ` ${component.unit}`}
        </strong>
        <ComponentSources answer={answer} claimIds={component.claimIds} />
      </article>
    );
  if (component.kind === "table")
    return (
      <article className="agent-generated-card agent-generated-table">
        <div className="agent-generated-table-wrap">
          <table>
            <caption>{component.title}</caption>
            <thead>
              <tr>
                {component.columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {component.rows.map((row, index) => (
                <tr key={index}>
                  {component.columns.map((column) => (
                    <td key={column.key}>{displayValue(row.values[column.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ComponentSources
          answer={answer}
          claimIds={component.rows.flatMap((row) => row.claimIds)}
        />
      </article>
    );
  if (component.kind === "chart")
    return (
      <article className="agent-generated-card agent-generated-chart">
        <h4>{component.title}</h4>
        <AgentChart component={component} />
        <ComponentSources
          answer={answer}
          claimIds={component.series.flatMap((series) =>
            series.points.flatMap((point) => point.claimIds),
          )}
        />
      </article>
    );
  if (component.kind === "comparison")
    return (
      <article className="agent-generated-card agent-generated-comparison">
        <h4>{component.title}</h4>
        <div className="agent-comparison-head">
          <span>Measure</span>
          <span>Baseline</span>
          <span>Scenario</span>
          <span>Change</span>
        </div>
        {component.items.map((item) => (
          <div className="agent-comparison-row" key={item.label}>
            <strong>{item.label}</strong>
            <span>
              {displayValue(item.baseline)} {item.unit}
            </span>
            <span>
              {displayValue(item.scenario)} {item.unit}
            </span>
            <span>{displayValue(item.delta)}</span>
          </div>
        ))}
        <ComponentSources
          answer={answer}
          claimIds={component.items.flatMap((item) => item.claimIds)}
        />
      </article>
    );
  return <AgentScenarioControls component={component} answer={answer} onOpenDraft={onOpenDraft} />;
}

function AgentChart({
  component,
}: {
  readonly component: Extract<AgentGeneratedUiComponent, { readonly kind: "chart" }>;
}) {
  const points = component.series.flatMap((series) =>
    series.points.map((point) => ({ ...point, series: series.name })),
  );
  const maximum = Math.max(1, ...points.map((point) => Math.abs(point.y)));
  if (component.chartType === "bar")
    return (
      <div className="agent-bar-chart">
        {points.map((point, index) => (
          <div className="agent-bar-row" key={`${point.series}:${point.x}:${index}`}>
            <span>{point.x}</span>
            <div>
              <i style={{ width: `${Math.max(1, (Math.abs(point.y) / maximum) * 100)}%` }} />
            </div>
            <strong>{point.y}</strong>
          </div>
        ))}
      </div>
    );
  const width = 560;
  const height = 180;
  const allY = points.map((point) => point.y);
  const minimumY = Math.min(...allY);
  const maximumY = Math.max(...allY);
  const span = maximumY - minimumY || 1;
  return (
    <div className="agent-line-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={component.title}>
        {component.series.map((series, seriesIndex) => {
          const coordinates = series.points
            .map(
              (point, index) =>
                `${series.points.length === 1 ? width / 2 : (index / (series.points.length - 1)) * width},${height - ((point.y - minimumY) / span) * (height - 20) - 10}`,
            )
            .join(" ");
          return (
            <g key={series.name}>
              <polyline
                points={coordinates}
                className={`agent-chart-series agent-chart-series-${seriesIndex % 4}`}
              />
              {series.points.map((point, index) => {
                const [x, y] = coordinates.split(" ")[index]!.split(",");
                return (
                  <circle key={`${point.x}:${index}`} cx={x} cy={y} r="4">
                    <title>
                      {series.name}: {point.x} · {point.y}
                    </title>
                  </circle>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="agent-chart-legend">
        {component.series.map((series) => (
          <span key={series.name}>{series.name}</span>
        ))}
      </div>
    </div>
  );
}

function AgentScenarioControls({
  component,
  answer,
  onOpenDraft,
}: {
  readonly component: Extract<AgentGeneratedUiComponent, { readonly kind: "scenario_controls" }>;
  readonly answer: AnswerSources;
  readonly onOpenDraft: (draft: AgentDraft) => void;
}) {
  const [values, setValues] = useState<Record<string, string | number>>(() =>
    Object.fromEntries(component.controls.map((control) => [control.key, control.defaultValue])),
  );
  const intent = answer.draftIntents[component.draftIntentIndex];
  if (intent === undefined) return null;
  return (
    <article className="agent-generated-card agent-scenario-controls">
      <h4>{component.title}</h4>
      <p>{component.description}</p>
      <div>
        {component.controls.map((control) => (
          <label key={control.key}>
            <span>{control.label}</span>
            {control.controlType === "select" ? (
              <select
                value={String(values[control.key])}
                onChange={(event) =>
                  setValues((current) => ({ ...current, [control.key]: event.target.value }))
                }
              >
                {control.options?.map((option) => (
                  <option value={option} key={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                min={control.minimum}
                max={control.maximum}
                step={control.step}
                value={Number(values[control.key])}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    [control.key]: Number(event.target.value),
                  }))
                }
              />
            )}
            <small>{control.unit}</small>
          </label>
        ))}
      </div>
      <button
        className="button button-primary"
        type="button"
        onClick={() => onOpenDraft(draftFromModelIntent(intent, values))}
      >
        Create and run draft
      </button>
      <small>
        Creates a governed draft and queues its targeted analysis. It does not publish the what-if
        or activate monitoring.
      </small>
    </article>
  );
}

function ComponentSources({
  answer,
  claimIds,
}: {
  readonly answer: Pick<AgentGroundedAnswer, "claims" | "citations">;
  readonly claimIds: readonly string[];
}) {
  const citations = [
    ...new Set(
      claimIds.flatMap(
        (claimId) => answer.claims.find((claim) => claim.claimId === claimId)?.citationIds ?? [],
      ),
    ),
  ]
    .map((citationId) => answer.citations.find((citation) => citation.citationId === citationId))
    .filter((citation): citation is AgentAnswerCitation => citation !== undefined);
  if (citations.length === 0) return null;
  return (
    <div className="agent-component-sources" aria-label="Component sources">
      {citations.map((citation) => (
        <CitationChip citation={citation} key={citation.citationId} />
      ))}
    </div>
  );
}

function displayValue(value: string | number | boolean | null | undefined): string {
  return value === undefined || value === null
    ? "—"
    : typeof value === "boolean"
      ? value
        ? "Yes"
        : "No"
      : String(value);
}

export function AgentGrounding({
  answer,
}: {
  readonly answer: NonNullable<AgentTurn["grounding"]>;
}) {
  const completedAt = new Date(answer.completedAt);
  const successfulTools = answer.toolRuns.filter((run) => run.status === "succeeded").length;
  return (
    <aside className="agent-grounding" aria-label="Answer sources and run status">
      {answer.citations.length === 0 ? null : (
        <div className="agent-citation-list" aria-label="Citations">
          {answer.citations.map((citation) => (
            <CitationChip citation={citation} key={citation.citationId} />
          ))}
        </div>
      )}
      {answer.limitations.length === 0 ? null : (
        <div className="agent-limitations">
          <strong>Limitations</strong>
          <ul>
            {answer.limitations.map((limitation) => (
              <li key={limitation.limitationId}>{limitation.message}</li>
            ))}
          </ul>
        </div>
      )}
      <details className="agent-run-meta">
        <summary>How this answer was produced</summary>
        <p>
          Run {answer.runId.slice(0, 8)} · {successfulTools}/{answer.toolRuns.length} tool calls
          succeeded ·{" "}
          {Number.isNaN(completedAt.valueOf()) ? answer.completedAt : completedAt.toLocaleString()}
        </p>
      </details>
    </aside>
  );
}

function CitationChip({ citation }: { readonly citation: AgentAnswerCitation }) {
  const href =
    citation.referenceType === "dataset"
      ? `/v1/datasets/${encodeURIComponent(citation.referenceId)}/provenance`
      : citation.referenceType === "document"
        ? `/v1/documents/${encodeURIComponent(citation.referenceId)}`
        : citation.referenceType === "analysis"
          ? `/v1/analysis-runs/${encodeURIComponent(citation.referenceId)}`
          : citation.sourceUrl;
  const asOf = citation.providerAsOf ?? citation.observedAt ?? citation.publishedAt;
  const label = `${citation.provider} · ${citation.title}`;
  const details = [
    citation.feedClass.replaceAll("_", " "),
    asOf === undefined ? undefined : `as of ${formatCitationTime(asOf)}`,
    citation.delaySeconds === undefined ? undefined : `${citation.delaySeconds}s delay`,
    citation.location?.kind === "characters"
      ? `characters ${citation.location.start}–${citation.location.end}`
      : undefined,
  ]
    .filter((item): item is string => item !== undefined)
    .join(" · ");
  return href === undefined ? (
    <span className="agent-citation-chip" title={details}>
      {label}
    </span>
  ) : (
    <a className="agent-citation-chip" href={href} target="_blank" rel="noreferrer" title={details}>
      {label}
    </a>
  );
}

function formatCitationTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}

export function parseHoldings(message: string): readonly ParsedHolding[] {
  const upper = message.toUpperCase();
  const tokens = upper.split(/[^A-Z0-9.,]+/).filter(Boolean);
  const parsed: ParsedHolding[] = [];

  tokens.forEach((token, index) => {
    const symbol = stripTrailingPunctuation(token);
    const instrument = INSTRUMENT_BY_SYMBOL.get(symbol);
    if (instrument === undefined) return;
    const quantity = quantityNearSymbol(tokens, index);
    if ((symbol.length <= 2 || GUARDED_SYMBOLS.has(symbol)) && quantity === undefined) return;
    if (parsed.some((holding) => holding.symbol === symbol)) return;
    parsed.push({ symbol, name: instrument.name, quantity });
  });

  const cashMatch = upper.match(
    /(\d[\d,.]*)\s*(?:USD\s*)?(?:IN\s+)?CASH|CASH\s*(?:OF|:)?\s*(\d[\d,.]*)/,
  );
  const cashQuantity = parseQuantity(cashMatch?.[1] ?? cashMatch?.[2]);
  if (cashQuantity !== undefined && !parsed.some((holding) => holding.symbol === "CASH")) {
    parsed.push({ symbol: "CASH", name: "Cash · USD", quantity: cashQuantity });
  }

  return parsed;
}

function quantityNearSymbol(tokens: readonly string[], symbolIndex: number): number | undefined {
  const direct = parseQuantity(tokens[symbolIndex - 1]) ?? parseQuantity(tokens[symbolIndex + 1]);
  if (direct !== undefined) return direct;
  if (/^SHARES?$/.test(tokens[symbolIndex - 1] ?? "")) {
    return parseQuantity(tokens[symbolIndex - 2]);
  }
  if (
    /^(?:OF|IN)$/.test(tokens[symbolIndex - 1] ?? "") &&
    /^SHARES?$/.test(tokens[symbolIndex - 2] ?? "")
  ) {
    return parseQuantity(tokens[symbolIndex - 3]);
  }
  return undefined;
}

export function routeAgentMessage(message: string): AgentReply {
  const lower = message.toLowerCase();
  if (
    /\b(add|hold|holding|holdings|position|positions|share|shares|own|bought|buy|ticker|etf)\b/.test(
      lower,
    )
  ) {
    return holdingsReply(message);
  }
  if (
    /\b(what.?if|scenario|stress|shock|crash|rates?|drop|fall|rise|bp|recession|inflation|selloff|happens)\b/.test(
      lower,
    )
  ) {
    return {
      text: "Stated as a what-if against the current portfolio. It arrives proposed, with the assumption it tests written out — an argument you can read, not a verdict.",
      draft: {
        title: "What-if drafted · S-0032 · proposed",
        lines: [
          `Frame: ${message.trim()}`,
          "Assumption under test · cross-position diversification holds through the shock",
          "Horizon 90 days · materiality threshold 1% of NAV",
          "No trade is proposed. The output is a transmission path and the evidence for and against it.",
        ],
        cta: "Create and run the what-if",
        hint: "Creates a governed draft and queues analysis; nothing is published",
        destination: "new_scenario",
        statedOrigin: message.trim(),
      },
    };
  }
  if (/\b(portfolio|book|create|start|new|set ?up|mandate)\b/.test(lower)) {
    return {
      text: "I can open the governed portfolio form. Its approved profile is locked to your organization, and it stays a draft and unmonitored until you publish inputs and explicitly take it live.",
      draft: {
        title: "Draft portfolio · not yet monitored",
        lines: [
          "Name and description are entered by you; the agent does not silently infer them",
          "Profile: the active approved profile in your organization",
          "Creating the draft does not begin monitoring, tripwires or coverage.",
        ],
        cta: "Open the draft portfolio",
        hint: "Nothing is monitored yet",
        destination: "create_book",
        statedOrigin: message.trim(),
      },
    };
  }
  if (/\b(report|summary|committee|pdf|pack|deliver)\b/.test(lower)) {
    return {
      text: "A report is assembled from pinned versions only, so every figure in it traces back to a saved portfolio version.",
      draft: {
        title: "Report outline · draft",
        lines: [
          "Built from the current portfolio version and its active what-ifs",
          "Limitations section is generated, not written by hand",
          "Approval and publication stay separate events.",
        ],
        cta: "Open Reports",
        hint: "Generation has not been queued",
        destination: "decision_packs",
      },
    };
  }
  if (/\b(risk|exposure|concentrat|crowd|worr|safe|danger|biggest|largest)/.test(lower)) {
    return {
      text: "I can show where concentration and transmission risk sit in the current portfolio without turning that reading into a trade recommendation.",
      draft: {
        title: "Where the exposure sits",
        lines: [
          "Open the current portfolio to inspect position size and concentration",
          "Coverage shows which what-ifs reach the exposure and which gaps remain",
          "A breach is a reading, not advice — the decision is recorded as yours.",
        ],
        cta: "Open the portfolio",
        hint: "Opens the current published view",
        destination: "workspace",
      },
    };
  }
  if (parseHoldings(message).length > 0) return holdingsReply(message);
  return {
    text: "I can do three things from here: stage holdings into a draft portfolio, turn a sentence into a what-if, or show you where an exposure sits. Which of those is this?",
  };
}

/**
 * ADR-090. Turns for a conversation reopened from the server. A question whose answer failed or is
 * still running says so instead of disappearing.
 */
export function turnsFromServerChat(chat: AgentChat): readonly AgentTurn[] {
  return chat.messages.flatMap((message): AgentTurn[] => {
    const question: AgentTurn = {
      id: `${message.askedAt}:${message.messageId}:user`,
      role: "user",
      text: message.question,
      ...(message.attachments === undefined || message.attachments.length === 0
        ? {}
        : { attachments: message.attachments.map((item) => ({ name: item.fileName })) }),
    };
    if (message.answer !== undefined) {
      const drafts = message.answer.draftIntents.map((intent) =>
        draftFromModelIntent(intent, undefined, message.question),
      );
      return [
        question,
        {
          id: `${message.answer.completedAt}:${message.messageId}:agent`,
          role: "agent",
          text: message.answer.answer,
          ...(drafts.length === 0 ? {} : { drafts }),
          grounding: message.answer,
        },
      ];
    }
    return [
      question,
      {
        id: `${message.askedAt}:${message.messageId}:agent`,
        role: "agent",
        text:
          message.status === "pending"
            ? "Polyfuture is still answering this question. Reopen the conversation in a minute."
            : "This question did not get an answer. Ask it again to retry.",
      },
    ];
  });
}

/** The rail's recent list: the server's conversations, with any newer local-only chat first. */
export function mergeAgentRecents(
  server: readonly AgentChatSummary[],
  local: readonly AgentRecentChat[],
  limit = 4,
): readonly AgentRecentChat[] {
  const byId = new Map<string, AgentRecentChat>();
  for (const chat of server) {
    byId.set(chat.chatId, { id: chat.chatId, title: chat.title, updatedAt: chat.lastActivityAt });
  }
  for (const chat of local) {
    const known = byId.get(chat.id);
    if (known === undefined || known.updatedAt < chat.updatedAt) {
      byId.set(chat.id, { ...chat, title: known?.title ?? chat.title });
    }
  }
  return [...byId.values()]
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
    .slice(0, limit);
}

export function loadAgentRecents(storageScope: string): readonly AgentRecentChat[] {
  return toRecents(readAgentStore(agentStorageKey(storageScope)));
}

export function draftForIntent(
  message: string,
  intent: AgentConversationIntent,
): AgentDraft | undefined {
  if (intent === "holdings") return holdingsReply(message).draft;
  if (intent === "what_if") {
    return {
      title: "What-if drafted · proposed",
      lines: [
        `Frame: ${message.trim()}`,
        "Assumptions, horizon and materiality remain editable before any run.",
        "No trade is proposed. The result will describe transmission paths and uncertainty.",
      ],
      cta: "Create and run the what-if",
      hint: "Creates a governed draft and queues analysis; nothing is published",
      destination: "new_scenario",
      statedOrigin: message.trim(),
    };
  }
  if (intent === "new_portfolio") {
    return {
      title: "Draft portfolio · not yet monitored",
      lines: [
        "Name and description are entered by you; the agent does not silently infer them",
        "Profile: the active approved profile in your organization",
        "Creating the draft does not begin monitoring, tripwires or coverage.",
      ],
      cta: "Open the draft portfolio",
      hint: "Nothing is monitored yet",
      destination: "create_book",
      statedOrigin: message.trim(),
    };
  }
  if (intent === "report") {
    return {
      title: "Report outline · draft",
      lines: [
        "Built from pinned portfolio and what-if versions",
        "Limitations and provenance remain visible",
        "Approval and publication stay separate events.",
      ],
      cta: "Open Reports",
      hint: "Generation has not been queued",
      destination: "decision_packs",
    };
  }
  if (intent === "exposure") {
    return {
      title: "Inspect the current exposure",
      lines: [
        "Open the selected portfolio to inspect concentration and position size",
        "Coverage shows which what-ifs reach the exposure and which gaps remain",
        "A risk reading is not a trade recommendation.",
      ],
      cta: "Open the portfolio",
      hint: "Opens the current governed view",
      destination: "workspace",
    };
  }
  return undefined;
}

function draftFromModelIntent(
  intent: AgentDraftIntent,
  controlValues?: Readonly<Record<string, string | number>>,
  statedOrigin?: string,
): AgentDraft {
  const basePayload = isAgentRecord(intent.payload) ? intent.payload : { value: intent.payload };
  const payload =
    controlValues === undefined ? basePayload : { ...basePayload, controls: controlValues };
  const destination: ActiveView =
    intent.intentType === "portfolio"
      ? "create_book"
      : intent.intentType === "position"
        ? "add_holdings"
        : intent.intentType === "assumption"
          ? "assumptions"
          : intent.intentType === "scenario" || intent.intentType === "what_if"
            ? "new_scenario"
            : intent.intentType === "trade"
              ? "trading"
              : intent.intentType === "evidence_contract"
                ? "evidence_new"
                : intent.intentType === "analysis"
                  ? "data"
                  : "decision_packs";
  const payloadLines = Object.entries(payload)
    .slice(0, 8)
    .map(([key, value]) => `${key.replaceAll("_", " ")} · ${agentPayloadValue(value)}`);
  const origin = firstText(
    statedOrigin,
    ...["statedOrigin", "frame", "thesis", "name", "prompt", "description"].map(
      (key) => payload[key],
    ),
  );
  const symbol =
    typeof payload["symbol"] === "string" ? payload["symbol"].toUpperCase() : undefined;
  const quantity = typeof payload["quantity"] === "number" ? payload["quantity"] : undefined;
  const holdings =
    intent.intentType === "position" && symbol !== undefined
      ? [{ symbol, name: symbol, quantity }]
      : undefined;
  return {
    title: `${intent.intentType.replaceAll("_", " ")} draft · review required`,
    lines:
      payloadLines.length === 0 ? ["The agent supplied an empty draft payload."] : payloadLines,
    cta:
      destination === "new_scenario"
        ? "Create and run scenario"
        : `Open ${intent.intentType.replaceAll("_", " ")} draft`,
    hint:
      destination === "new_scenario"
        ? "Creates a governed draft and queues analysis · nothing is published"
        : `Destination preview · ${intent.destination} · nothing is published`,
    destination,
    payload,
    ...(origin === undefined ? {} : { statedOrigin: origin }),
    ...(holdings === undefined ? {} : { holdings }),
  };
}

/**
 * Convert a model-authored, non-executable draft intent into the bounded input accepted by the
 * governed what-if API. Clicking the agent card is the user's explicit command to persist and run
 * the draft; publication and monitoring remain separate lifecycle decisions.
 */
export function scenarioInputForAgentDraft(draft: AgentDraft): ScenarioDraftInput | undefined {
  if (draft.destination !== "new_scenario") return undefined;
  const payload = isAgentRecord(draft.payload) ? draft.payload : {};
  const controls = isAgentRecord(payload["controls"]) ? payload["controls"] : {};
  const assets = stringArray(controls["assets"] ?? payload["assets"]);
  const horizon = scalarText(controls["horizon"] ?? payload["horizon"]);
  const priceMove = numberRange(controls["priceMovePercent"] ?? payload["priceMovePercent"]);
  const explicitOrigin = firstText(
    draft.statedOrigin,
    payload["statedOrigin"],
    payload["frame"],
    payload["thesis"],
    payload["shock"],
    payload["prompt"],
    payload["description"],
  );
  const synthesizedOrigin = describeStructuredWhatIf(assets, horizon, priceMove);
  const thesis = oneLine(explicitOrigin ?? synthesizedOrigin ?? "");
  if (thesis.length === 0 || thesis.length > 200) return undefined;

  const suppliedFalsification = firstText(payload["falsification"], payload["falsificationText"]);
  const falsification = oneLine(
    suppliedFalsification ??
      `The stated change does not occur${horizon === undefined ? "" : ` within ${horizon}`}.`,
  );
  const notes = agentScenarioNotes(payload, assets, horizon, priceMove);
  return {
    name: compactAgentScenarioTitle(thesis),
    thesis,
    polarity: scenarioPolarity(payload["polarity"], priceMove),
    falsification: falsification.slice(0, 500),
    ...(notes.length === 0 ? {} : { notes }),
    startElaboration: true,
  };
}

/**
 * Recover explicit holdings carried by a scenario draft so an empty portfolio can enter the
 * governed holdings-review flow before the scenario is persisted. The model payload is still
 * treated as untrusted display data: only the same bounded ticker/quantity parser used by the
 * ordinary holdings intent may produce a draft holding.
 */
export function holdingsForAgentScenarioDraft(draft: AgentDraft): readonly ParsedHolding[] {
  if (draft.destination !== "new_scenario") return [];
  if (draft.holdings !== undefined && draft.holdings.length > 0) return draft.holdings;
  const payload = isAgentRecord(draft.payload) ? draft.payload : {};
  const statedPositions = stringArray(
    payload["positionsConsidered"] ?? payload["positions_considered"],
  );
  return parseHoldings(statedPositions.join(", "));
}

function describeStructuredWhatIf(
  assets: readonly string[],
  horizon: string | undefined,
  priceMove: readonly number[] | undefined,
): string | undefined {
  const subject = assets.length === 0 ? undefined : assets.join(" and ");
  const movement =
    priceMove === undefined
      ? undefined
      : priceMove.length === 1
        ? `${formatSignedPercent(priceMove[0]!)}`
        : `${formatSignedPercent(priceMove[0]!)} to ${formatSignedPercent(priceMove.at(-1)!)}`;
  if (subject === undefined && movement === undefined && horizon === undefined) return undefined;
  const verb = assets.length > 1 ? "move" : "moves";
  return [
    `What if ${subject ?? "the selected exposure"}${movement === undefined ? " changes" : ` ${verb} ${movement}`}`,
    horizon === undefined ? undefined : `over ${horizon}`,
  ]
    .filter((part): part is string => part !== undefined)
    .join(" ")
    .concat("?");
}

function agentScenarioNotes(
  payload: Readonly<Record<string, unknown>>,
  assets: readonly string[],
  horizon: string | undefined,
  priceMove: readonly number[] | undefined,
): string {
  const notes = [
    "Created from an explicit agent draft action.",
    assets.length === 0 ? undefined : `Assets: ${assets.join(", ")}`,
    horizon === undefined ? undefined : `Horizon: ${horizon}`,
    priceMove === undefined ? undefined : `Price move percent: ${priceMove.join(" to ")}`,
    Object.keys(payload).length === 0
      ? undefined
      : `Agent draft payload: ${JSON.stringify(payload)}`,
  ]
    .filter((part): part is string => part !== undefined)
    .join("\n");
  return notes.slice(0, 2_000);
}

function scenarioPolarity(
  value: unknown,
  priceMove: readonly number[] | undefined,
): ScenarioPolarity {
  if (value === "negative" || value === "positive" || value === "mixed") return value;
  if (priceMove === undefined || priceMove.length === 0) return "mixed";
  if (priceMove.every((item) => item <= 0)) return "negative";
  if (priceMove.every((item) => item >= 0)) return "positive";
  return "mixed";
}

export function compactAgentScenarioTitle(value: string): string {
  const firstClause = value.split(/[.!?]/, 1)[0]?.trim() ?? value;
  return firstClause.length <= 120 ? firstClause : `${firstClause.slice(0, 117).trimEnd()}…`;
}

function firstText(...values: readonly unknown[]): string | undefined {
  return values.find(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );
}

function stringArray(value: unknown): readonly string[] {
  if (Array.isArray(value))
    return value.filter((item): item is string => typeof item === "string" && item.length > 0);
  return typeof value === "string" && value.trim().length > 0 ? [value.trim()] : [];
}

function scalarText(value: unknown): string | undefined {
  return typeof value === "string" || typeof value === "number" ? String(value).trim() : undefined;
}

function numberRange(value: unknown): readonly number[] | undefined {
  const values = (Array.isArray(value) ? value : [value]).filter(
    (item): item is number => typeof item === "number" && Number.isFinite(item),
  );
  return values.length === 0 ? undefined : [...values].sort((left, right) => left - right);
}

function formatSignedPercent(value: number): string {
  return `${value > 0 ? "+" : ""}${value}%`;
}

function oneLine(value: string): string {
  return value
    .replace(/[\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isAgentRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function agentPayloadValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
    return String(value);
  const serialized = JSON.stringify(value);
  return serialized.length > 240 ? `${serialized.slice(0, 237)}…` : serialized;
}

function holdingsReply(message: string): AgentReply {
  const holdings = parseHoldings(message);
  const lines =
    holdings.length > 0
      ? [
          ...holdings.map((holding) =>
            holding.symbol === "CASH"
              ? `Cash · USD ${formatInteger(holding.quantity!)}`
              : `${holding.symbol} · ${
                  holding.quantity === undefined
                    ? "quantity not stated"
                    : `${formatInteger(holding.quantity)} shares`
                } · cost basis missing`,
          ),
          "Quantities came from your message. No portfolio value or cost basis was inferred.",
        ]
      : [
          "Nothing in your message matched a listed symbol, so the draft opens empty.",
          "Search by name or symbol on the next screen, or type the holding by hand.",
        ];
  return {
    text:
      holdings.length > 0
        ? `Read as ${holdings.length} ${holdings.length === 1 ? "line" : "lines"}. I will stage them as a draft — nothing is in a monitored portfolio until you publish it.`
        : "I could not match a listed symbol in that. Add positions is the right screen; you can search there.",
    draft: {
      title:
        holdings.length > 0
          ? `Draft holdings · ${holdings.length} staged`
          : "Add positions · empty draft",
      lines,
      cta: "Open the draft in Add positions",
      hint: "Confirm each line before publishing",
      destination: "add_holdings",
      holdings,
      statedOrigin: message.trim(),
    },
  };
}

function parseQuantity(value: string | undefined): number | undefined {
  if (value === undefined || !/^\d[\d,.]*$/.test(value)) return undefined;
  const parsed = Number(stripTrailingPunctuation(value).replaceAll(",", ""));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function stripTrailingPunctuation(value: string): string {
  return value.replace(/[.,]+$/, "");
}

function formatInteger(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** The question an answer replied to: the nearest user turn before it. */
function questionBefore(turns: readonly AgentTurn[], index: number): string {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    if (turns[cursor]!.role === "user") return turns[cursor]!.text;
  }
  return "Research result";
}

function firstUserMessage(turns: readonly AgentTurn[]): string {
  return turns.find((turn) => turn.role === "user")?.text.slice(0, 80) ?? "New chat";
}

function agentStorageKey(scope: string): string {
  return `polyfuture-agent-chats:${scope}`;
}

function toRecents(store: AgentStore): readonly AgentRecentChat[] {
  return store.chats.slice(0, 4).map(({ id, title, updatedAt }) => ({ id, title, updatedAt }));
}

function readAgentStore(key: string): AgentStore {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return { chats: [] };
    const parsed = JSON.parse(raw) as Partial<AgentStore>;
    return Array.isArray(parsed.chats) ? { chats: parsed.chats as StoredChat[] } : { chats: [] };
  } catch {
    return { chats: [] };
  }
}

function writeAgentStore(key: string, store: AgentStore): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(store));
  } catch {
    // Persistence is optional in privacy-restricted and opaque-origin contexts.
  }
}

function persistChat(
  storageKey: string,
  chatId: string,
  turns: readonly AgentTurn[],
  updatedAt: string,
  onChatsChanged: (recents: readonly AgentRecentChat[]) => void,
): void {
  const store = readAgentStore(storageKey);
  const chat: StoredChat = {
    id: chatId,
    title: firstUserMessage(turns),
    updatedAt,
    turns,
  };
  const nextStore: AgentStore = {
    chats: [chat, ...store.chats.filter((item) => item.id !== chatId)].slice(0, 20),
  };
  writeAgentStore(storageKey, nextStore);
  onChatsChanged(toRecents(nextStore));
}
